<template>
    <div class="monthly-lifetime-block">
        <ul class="graph-payment-block">
            <li>
                <div class="amt-block">
                    <h3 class="title">Monthly Payment</h3>
                    <div class="amt-val">
                        <span>{{ absoluteWithBrackets(CurrentMonthlyPayment) }}</span>
                        <small>vs</small>
                        <b :class="isGreater(CurrentMonthlyPayment, RefMonthlyPayment) ? 'green-text' : 'red-text'">{{ absoluteWithBrackets(RefMonthlyPayment) }}</b>
                    </div>
                    <span v-if="!isNegative(TotalMonthlySavings)" class="up-down-val"><i class="lt4-ArrowDownCircle"></i> Lower your payment by {{ absolute(TotalMonthlySavings) }}</span>
                    <span v-else class="up-down-val increases"><i class="lt4-ArrowUpCircle"></i> Increased your payment by {{ absolute(TotalMonthlySavings) }}</span>
                </div>
            </li>
            <li>
                <div class="amt-block">
                    <h3 class="title">Interest Rate</h3>
                    <div class="amt-val">
                        <span>{{ currentInterestRate }}%</span>
                        <small>vs</small>
                        <b :class="isGreater(currentInterestRate, refInterestRate) ? 'green-text' : 'red-text'">{{ refInterestRate }}%</b>
                    </div>
                    <div v-if="!isInterestRateMessageVisible">
                        <span v-if="isGreater(currentInterestRate,refInterestRate)" class="up-down-val"><i class="lt4-ArrowDownCircle"></i> Lowers your interest rate</span>
                        <span v-else class="up-down-val increases"><i class="lt4-ArrowUpCircle"></i> Increases your interest rate</span>
                    </div>
                </div>
            </li>
            <li>
                <div class="amt-block">
                    <h3 class="title">Payoff Time</h3>
                    <div class="amt-val">
                        <span>{{ CurrentPayOffTime }} years</span>
                        <small>vs</small>
                        <b :class="isGreater(CurrentPayOffTime,RefPayOffTime) ? 'green-text' : 'red-text'">{{ RefPayOffTime }} years</b>
                    </div>
                    <span v-if="isGreater(CurrentPayOffTime,RefPayOffTime)" class="up-down-val"><i class="lt4-ArrowDownCircle"></i> Lower your payoff time</span>
                    <span v-else class="up-down-val increases"><i class="lt4-ArrowUpCircle"></i> Increases your payoff time</span>
                </div>
            </li>
            <li>
                <div class="amt-block">
                    <h3 class="title">Total Loan Cost</h3>
                    <div class="amt-val">
                        <span>{{ absoluteWithBrackets(CurrentLoanCost) }}</span>
                        <small>vs</small>
                        <b :class="isGreater(CurrentLoanCost,RefLoanCost) ? 'green-text' : 'red-text'">{{ absoluteWithBrackets(RefLoanCost) }}</b>
                    </div>
                    <span v-if="isNegative(TotalLoanCostSavings)" class="up-down-val increases"><i class="lt4-ArrowUpCircle"></i> Increases {{ absolute(TotalLoanCostSavings) }} in lifetime costs</span>
                    <span v-else class="up-down-val"><i class="lt4-ArrowDownCircle"></i> Save {{ absolute(TotalLoanCostSavings) }} in lifetime costs</span>
                </div>
            </li>
        </ul>
        <div class="monthly-lifetime-tab">
            <ul class="tab-name">
                <li :class="{'active': isMonthly}" @click="updateTabValues(true)">
                    <h2>Monthly cost analysis</h2>
                </li>
                <li :class="{'active': !isMonthly}" @click="updateTabValues(false)">
                    <h2>Lifetime cost analysis</h2>
                </li>
            </ul>
            <div class="tab-content">
                <div class="row active">
                    <div class="refinancing-block">
                        <ul class="refinance-graph">
                            <li>
                                <h3>{{ savingSectionName }}</h3>
                                <span v-if="isMonthly" :class="isNegative(TotalMonthlySavings) ? 'save-amt red-text' : 'save-amt green-text'">{{ absoluteWithBrackets(TotalMonthlySavings) }}<span>/mo</span></span>
                                <span v-else :class="isNegative(TotalLoanCostSavings) ? 'save-amt red-text' : 'save-amt green-text'">{{ absoluteWithBrackets(TotalLoanCostSavings) }}</span>
                                <button class="btn btn-blue" type="submit" @click="redirect()">{{ destinationButtonText }}</button>
                                <div>
                                    <advertising-disclosures
                                        v-if="disclosureText"
                                        :disclosure-text="disclosureText"
                                        :disclosure-tokens="disclosureTokens"
                                    />
                                </div>
                                <lt-logo v-if="ltLogo==1">
                                    <small>NMLS#1136 Terms & Conditions Apply</small>
                                </lt-logo>
                            </li>
                            <li>
                                <div class="chart-block">
                                    <div ref="canvasWrapper">
                                        <vue-chart type="bar" :data="chartData" :options="options" :width="ChartWidth" :height="ChartHeight" />
                                        <div v-if="chartData.datasets[0].data.length > 0" id="chartjs-tooltip-break-even" ref="breakEvenTooltip" style="position: absolute;">
                                            <b>{{ (chartData.datasets[0].data[0].x - 1) * 12 }} months</b>
                                            <span>
                                                Break-Even Point
                                                <tooltip-view label="Break-Even Point" :show-question-mark="true" :popper-content="tooltipContent" referenceElement="open me" />
                                            </span>
                                        </div>
                                        <div id="chartjs-tooltip-years" ref="yearsTooltip" style="position: absolute;">
                                            {{ planStayInYears }} {{ planStayInYears === 1 ? 'Year' : 'Years' }}
                                        </div>
                                    </div>
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
    import VueChart from 'vue-chart-js';
    import Accounting from 'accounting';
    import LtLogo from '~/components/lt-logo.vue';
    import AdvertisingDisclosures from '~/components/advertising-disclosures.vue';
    import TooltipView from '~/components/tooltip-view.vue';
    import BaseMixin from '~/mixin/base-mixin';
    import AmortizationBreakdownHelper from '~/helpers/mortgage-amortization-breakdown';

    export default {
        props: {

            disclosureTokens: {
                default: '',
            },
            disclosureText: {
                default: '',
            },
            ltLogo: {
                default: 0,
            },
            destinationLink: {
                default: '',
            },
            destinationButtonText: {
                default: '',
            },
            originalAmount: {
                default: 0,
            },
            currentLoanTermInYears: {
                default: 0,
            },
            refLoanTermInYears: {
                default: 0,
            },
            currentInterestRate: {
                default: 0,
            },
            refInterestRate: {
                default: 0,
            },
            planStayInYears: {
                type: Number,
                default: 0,
            },
            lenderFees: {
                default: 0,
            },
            pmiInPerc: {
                default: 0.625,
            },
            startDate: {
                default: new Date(),
            },
            propertyValue: {
                default: 0,
            },
        },
        data() {
            return {
                isMonthly: true,
                YearsYouPlanToStay: 0,
                CurrentMonthlyPayment: 0,
                TotalLoanPayment: 0,
                RefMonthlyIntPrin: 0,
                CurrentDownPayment: 0,
                RefDownPayment: 0,
                CurrentLoanCost: 0,
                RefMonthlyPayment: 0,
                RefLoanCost: 0,
                RefOneTimeExpense: 0,
                CurrentPayOffTime: 0,
                RefPayOffTime: 0,
                TotalMonthlySavings: 0,
                TotalLoanCostSavings: 0,
                CurrentAmortization: [],
                RefAmortization: [],
                CurrentMortgageAmount: 0,
                RefMortgageAmount: 0,
                InterestSavings: [],
                ChartWidth: 750,
                ChartHeight: 200,
                chartData: {
                    labels: [],
                    datasets: [
                        {
                            label: 'BREAK-EVEN TOOLTIP',
                            backgroundColor: '#2087c1',
                            hoverBackgroundColor: '#2087c1',
                            borderColor: '#fff',
                            borderWidth: 3,
                            hoverBorderWidth: 3,
                            hoverRadius: 0,
                            hoverBorderColor: '#fff',
                            data: [],
                            type: 'bubble',
                        },
                        {
                            label: 'YEARS TOOLTIP',
                            backgroundColor: '#666',
                            hoverBackgroundColor: '#666',
                            borderColor: '#fff',
                            borderWidth: 3,
                            hoverBorderWidth: 3,
                            hoverBorderColor: '#fff',
                            hoverRadius: 0,
                            data: [],
                            type: 'bubble',
                        },
                        {
                            label: 'Total Savings',
                            backgroundColor: [],
                            borderColor: [],
                            hoverBackgroundColor: [],
                            data: [],
                            categoryPercentage: 0.9,
                            barPercentage: 0.9,
                        },
                    ],
                    borderWidth: 1,
                },
                options: {
                    scaleShowLabel: false,
                    cutoutPercentage: 89,
                    responsive: true,
                    maintainAspectRatio: false,
                    legend: {
                        display: false,
                    },
                    intersect: true,
                    tooltips: {
                        enabled: false,
                        mode: 'nearest',
                        custom(tooltip) {
                            let tooltipEl = document.getElementById('chartjs-tooltip');

                            if (tooltipEl) {
                                tooltipEl.parentNode.removeChild(tooltipEl);
                            }

                            tooltipEl = document.createElement('div');
                            tooltipEl.id = 'chartjs-tooltip';
                            tooltipEl.innerHTML = '<div></div>';
                            this._chart.canvas.parentNode.appendChild(tooltipEl);

                            if (Number(tooltip.opacity) === 0) {
                                tooltipEl.style.opacity = 0;
                                return;
                            }
                            tooltip.displayColors = false;
                            tooltipEl.classList.remove('above', 'below', 'no-transform');
                            if (tooltip.yAlign) {
                                tooltipEl.classList.add(tooltip.yAlign);
                            } else {
                                tooltipEl.classList.add('no-transform');
                            }
                            function getBody(bodyItem) {
                                return bodyItem.lines;
                            }
                            // eslint-disable-next-line eqeqeq
                            if (tooltip.dataPoints[0].datasetIndex == 0 || tooltip.dataPoints[0].datasetIndex == 1) {
                                tooltipEl.style.opacity = 0;
                                return;
                            }
                            if (tooltip.body) {
                                let innerHtml = '';
                                const titleLines = tooltip.title || [];
                                const bodyLines = tooltip.body.map(getBody);
                                bodyLines.forEach((body) => {
                                    const bodySplit = body[0].split(':');
                                    const labelSplit = bodySplit[0].split('$');
                                    innerHtml += `<span>${labelSplit[0]}</span>`;
                                    innerHtml += `<h3>${Accounting.formatMoney(bodySplit[1], { precision: 0 })}</h3>`;
                                    // eslint-disable-next-line eqeqeq
                                    const breakyear = titleLines == 1 ? 'Year' : 'Years';
                                    innerHtml += `<b>${titleLines} ${breakyear}</b>`;
                                });
                                const tableRoot = tooltipEl.querySelector('div');
                                tableRoot.innerHTML = innerHtml;
                            }
                            const positionY = this._chart.canvas.offsetTop;
                            const positionX = this._chart.canvas.offsetLeft;
                            tooltipEl.style.opacity = '1';
                            tooltipEl.style.left = `${positionX + tooltip.caretX}px`;
                            tooltipEl.style.top = `${positionY - 110 + tooltip.caretY}px`;
                        },
                    },
                    scales: {
                        yAxes: [{
                            height: 10,
                            ticks: {
                                display: false,
                            },
                            gridLines: {
                                lineWidth: 10,
                                zeroLineWidth: 1,
                                color: 'rgba(0, 0, 0, 0)',
                                zeroLineColor: '#d3d3d3',
                                tickMarkLength: 0,
                            },
                            stacked: true,
                        }],
                        xAxes: [{
                            ticks: {
                                display: false,
                            },
                            gridLines: {
                                lineWidth: 90,
                                zeroLineWidth: 0,
                                color: 'rgba(0, 0, 0, 0)',
                            },
                            stacked: true,
                        }],
                    },
                },
                DestinationLinkOrId: this.destinationLink,
                SumOfSaving: 0,
            };
        },
        methods: {
            updateTabValues(isMonthlyValue) {
                this.isMonthly = isMonthlyValue;
            },
            pushDataInChart() {
                const label = [];
                this.chartData.labels = [];
                this.chartData.datasets[0].data = [];
                this.chartData.datasets[1].data = [];
                this.chartData.datasets[2].data = [];
                this.chartData.datasets[2].backgroundColor = [];
                this.chartData.datasets[2].borderColor = [];
                this.chartData.datasets[2].hoverBackgroundColor = [];
                const lableCount = this.currentLoanTermInYears > this.refLoanTermInYears ? this.currentLoanTermInYears : this.refLoanTermInYears;
                for (let i = 1; i <= lableCount; i++) {
                    label.push(i);
                }
                const data = this.InterestSavings;

                // Find the first break-even point (first positive value)
                const breakEvenIndex = data.findIndex(value => value > 0);
                if (breakEvenIndex !== -1) {
                    const subArray = { x: label[breakEvenIndex], y: 0, r: 8 };
                    this.chartData.datasets[0].data.push(subArray);
                }

                for (let i = 0; i < label.length; i++) {
                    this.chartData.labels.push(label[i]);
                    this.chartData.datasets[2].data.push(data[i]);
                    if (data[i] > 0) {
                        this.chartData.datasets[2].backgroundColor.push('#08c177');
                        this.chartData.datasets[2].borderColor.push('#08c177');
                    } else {
                        this.chartData.datasets[2].backgroundColor.push('#d93551');
                        this.chartData.datasets[2].borderColor.push('#d93551');
                    }
                    if (this.planStayInYears - 1 === i) {
                        const subArray = { x: label[i], y: 0, r: 8 };
                        this.chartData.datasets[1].data.push(subArray);
                    }
                }
            },
            registerPlugin() {
                // Cannot use arrow function here because the ChartJS is loosing context
                const self = this;
                Chart.plugins.register({
                    afterRender(chart) {
                        const style = window.getComputedStyle(self.$refs.canvasWrapper, null);

                        const tooltipElBreak = self.$refs.breakEvenTooltip;
                        const data0 = chart.config.data.datasets[0]._meta[0].data[0];

                        if (self.chartData.datasets[0].data.length > 0 && data0 && tooltipElBreak) {
                            const paddingLeft = parseInt(style.getPropertyValue('padding-left'), 10);
                            tooltipElBreak.style.left = `${data0._model.x - tooltipElBreak.offsetWidth / 2 + paddingLeft}px`;
                            tooltipElBreak.style.top = `${data0._model.y - 130}px`;
                        }

                        const data1 = chart.config.data.datasets[1]._meta[0].data[0];
                        const tooltipElYears = self.$refs.yearsTooltip;

                        if (data1 && tooltipElYears) {
                            const paddingLeft = parseInt(style.getPropertyValue('padding-left'), 10);
                            tooltipElYears.style.left = `${data1._model.x - tooltipElYears.offsetWidth / 2 + paddingLeft}px`;
                            tooltipElYears.style.top = `${data1._model.y - 79}px`;
                        }
                    },
                });
            },
            calculate() {
                this.InterestSavings = [];
                this.SumOfSaving = 0;
                this.CurrentMortgageAmount = this.originalAmount;
                this.CurrentDownPayment = (this.propertyValue - this.CurrentMortgageAmount) / this.propertyValue * 100;

                const date = new Date();
                const year = date.getFullYear();
                const month = date.getMonth();
                const termPaid = this.getDateMonthDifference(this.startDate, date);
                const monthOffset = date.getDate() < this.startDate.getDate() ? -1 : 0;

                const previousDate = new Date();
                let requiredMonth = Math.max(0, month - 1);
                previousDate.setMonth(requiredMonth);
                previousDate.setDate(1);
                if (date.getDate() < this.startDate.getDate()) {
                    requiredMonth -= 1;
                }
                const currentMortgageStartDate = new Date(this.startDate);
                currentMortgageStartDate.setMonth(currentMortgageStartDate.getMonth() + 1);

                const currentData = AmortizationBreakdownHelper.amortizationBreakdown(this.CurrentMortgageAmount, this.currentLoanTermInYears, this.currentInterestRate, this.propertyValue, 0, 0, this.CurrentDownPayment > 20 ? 1 : 0, this.startDate);
                this.CurrentAmortization = currentData.amortizationsByYear;

                if (this.CurrentAmortization && this.CurrentAmortization[year] && this.CurrentAmortization[year].monthsDetail && this.CurrentAmortization[year].monthsDetail[requiredMonth]) {
                    this.RefMortgageAmount = this.CurrentAmortization[year].monthsDetail[requiredMonth].balance;
                    this.CurrentMonthlyPayment = this.CurrentAmortization[year].monthsDetail[requiredMonth].payment;
                }
                this.RefDownPayment = (this.propertyValue - this.RefMortgageAmount) / this.propertyValue * 100;

                const refOneTimeExpense = this.propertyValue * (this.lenderFees / 100);
                const refData = AmortizationBreakdownHelper.amortizationBreakdown(
                    this.RefMortgageAmount,
                    this.refLoanTermInYears,
                    this.refInterestRate,
                    this.propertyValue,
                    0,
                    0,
                    this.RefDownPayment > 20 ? 1 : 0,
                    date,
                );
                this.LenderTilesFees = this.RefMortgageAmount * this.lenderFees / 100;
                this.RefOneTimeExpense = refOneTimeExpense;
                this.RefAmortization = refData.amortizationsByYear;

                if (this.RefAmortization[year] && this.RefAmortization[year].monthsDetail) {
                    for (let j = 0; j < this.RefAmortization[year].monthsDetail.length; j++) {
                        // eslint-disable-next-line eqeqeq
                        if (this.RefAmortization[year].monthsDetail[j].month == month) {
                            this.RefMonthlyPayment = this.RefAmortization[year].monthsDetail[j].payment;
                            break;
                        }
                    }
                }

                const currentDetails = AmortizationBreakdownHelper.amortizationBreakdown(
                    this.CurrentMortgageAmount,
                    this.currentLoanTermInYears,
                    this.currentInterestRate,
                    this.propertyValue,
                    0,
                    0,
                    this.CurrentDownPayment > 20 ? 1 : 0,
                    currentMortgageStartDate,
                );
                const currentMortgageData = currentDetails.amortizationsByYear;
                const refGraphData = AmortizationBreakdownHelper.amortizationBreakdown(
                    this.RefMortgageAmount,
                    this.refLoanTermInYears,
                    this.refInterestRate,
                    this.propertyValue,
                    0,
                    0,
                    this.RefDownPayment > 20 ? 1 : 0,
                    monthOffset === 0 ? date : previousDate,
                );
                const count = this.currentLoanTermInYears > this.refLoanTermInYears ? this.currentLoanTermInYears : this.refLoanTermInYears;
                const currentGraphDataAmortization = {};
                const refGraphDataAmortization = refGraphData.amortizationsByYear;

                Object.keys(currentMortgageData).forEach((key) => {
                    if (key >= 2019) {
                        currentGraphDataAmortization[key] = currentMortgageData[key];
                    }
                });

                for (let i = 0; i < count; i++) {
                    let currentMonthsDetail = 0;
                    let totalSaving = 0;
                    if (currentGraphDataAmortization && currentGraphDataAmortization[year + i]) {
                        const startPoint = i === 0 ? month + monthOffset : 0;
                        currentMonthsDetail = currentGraphDataAmortization[year + i].monthsDetail.slice(startPoint);
                    }
                    const refMonthDetail = refGraphDataAmortization && refGraphDataAmortization[year + i] ? refGraphDataAmortization[year + i].monthsDetail : 0;
                    totalSaving = this.getTotalSaving(currentMonthsDetail, refMonthDetail);
                    this.InterestSavings.push(totalSaving);
                }

                let payOffDiff = 0;
                if (this.startDate) {
                    payOffDiff = year - this.startDate.getFullYear();
                }
                const currentMortgagePaid = termPaid * this.CurrentMonthlyPayment;
                this.CurrentLoanCost = currentData.AllTotalInterest + currentData.AllTotalPrincipal - currentMortgagePaid;
                this.RefLoanCost = refData.AllTotalInterest + refData.AllTotalPrincipal + this.RefMortgageAmount * this.lenderFees / 100;
                this.TotalMonthlySavings = Math.round(this.CurrentMonthlyPayment) - Math.round(this.RefMonthlyPayment);
                this.TotalLoanCostSavings = Math.round(this.CurrentLoanCost) - Math.round(this.RefLoanCost);
                this.CurrentPayOffTime = this.currentLoanTermInYears - payOffDiff;
                this.RefPayOffTime = parseInt(this.refLoanTermInYears, 10);

                this.isMonthly = this.isNegative(this.TotalLoanCostSavings);
                this.pushDataInChart();
            },
            getSumOfInterestForYear(monthsDetail) {
                return Object.keys(monthsDetail).reduce((sum, month) => {
                    if (monthsDetail && monthsDetail[month]) {
                        return sum + monthsDetail[month].interestPaid;
                    }
                    return sum;
                }, 0);
            },
            getTotalSaving(currentMonthsDetail, refMonthDetail) {
                let totalSaving = 0;
                let currentInterestSum = 0;
                let newInterestSum = 0;
                currentInterestSum = this.getSumOfInterestForYear(currentMonthsDetail);
                newInterestSum = this.getSumOfInterestForYear(refMonthDetail);
                this.SumOfSaving += currentInterestSum - newInterestSum;
                totalSaving = this.SumOfSaving - this.LenderTilesFees;
                return totalSaving;
            },
            absolute(figure) {
                return Accounting.formatMoney(Math.abs(figure), { precision: 0 });
            },
            absoluteWithBrackets(figure) {
                const value = Accounting.formatMoney(Math.abs(figure), { precision: 0 });
                return this.isNegative(figure) ? `(${value})` : value;
            },
            isNegative(figure) {
                return figure < 0;
            },
            isGreater(value1, value2) {
                return parseFloat(value1) > parseFloat(value2);
            },
            getDateMonthDifference(from, to) {
                let months = to.getMonth() - from.getMonth() + 12 * (to.getFullYear() - from.getFullYear());
                if (to.getDate() < from.getDate()) {
                    const newFrom = new Date(to.getFullYear(), to.getMonth(), from.getDate());
                    if (to < newFrom && to.getMonth() === newFrom.getMonth() && to.getYear() % 4 !== 0) {
                        months--;
                    }
                }

                return months;
            },
        },
        mounted() {
            this.registerPlugin();
            this.calculate();
        },
        computed: {
            isInterestRateMessageVisible() {
                return parseFloat(this.currentInterestRate) === parseFloat(this.refInterestRate);
            },
            savingSectionName() {
                return this.isMonthly ? 'Monthly payment savings' : 'Lifetime savings';
            },
            tooltipContent() {
                return `<div class="popup-content">A <a href="https://www.lendingtree.com/home/mortgage/how-to-find-the-breakeven-on-your-refinance/" target="_blank" rel="nofollow">refinance break-even point</a> is when you've saved enough money on your monthly payments to cover what you paid in closing costs. If refinancing costs you $3,000 upfront but saves you $100 per month, you'll break even after 30 months when your total savings reach $3,000.</div>`;
            },
            calculationInputs() {
                // all important inputs in one place
                return {
                    originalAmount: this.originalAmount,
                    currentInterestRate: this.currentInterestRate,
                    refInterestRate: this.refInterestRate,
                    currentLoanTermInYears: this.currentLoanTermInYears,
                    refLoanTermInYears: this.refLoanTermInYears,
                    startDate: this.startDate,
                    planStayInYears: this.planStayInYears,
                    lenderFees: this.lenderFees,
                    propertyValue: this.propertyValue,
                };
            },
        },
        watch: {
            // whatch them all and trigger recalculation
            calculationInputs() {
                this.calculate();
            },
        },
        components: {
            TooltipView,
            VueChart,
            LtLogo,
            AdvertisingDisclosures,
        },
        mixins: [BaseMixin],
    };
</script>
<style src="./refinancing-makes-sense.scss" lang="scss"></style>
